import React from 'react';
import { Link } from 'react-router-dom';
import { Utensils } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Menu = () => {
  const [heroRef, heroVisible] = useScrollAnimation(0.1);
  const [menuRef, menuVisible] = useScrollAnimation(0.2);

  const dishes = [
    {
      id: "dish-1",
      name: "Jerk Chicken",
      description: "Authentic Caribbean spices with a perfect kick of heat.",
      category: "Main"
    },
    {
      id: "dish-2",
      name: "Oxtail",
      description: "Slow-cooked to tender perfection in rich gravy.",
      category: "Main"
    },
    {
      id: "dish-3",
      name: "Roast Lamb",
      description: "Succulent and seasoned with traditional herbs.",
      category: "Main"
    },
    {
      id: "dish-4",
      name: "Curry Goat",
      description: "Caribbean delicacy, rich and flavourful.",
      category: "Main"
    },
    {
      id: "dish-5",
      name: "Fried Chicken",
      description: "Golden, crispy, and perfectly seasoned.",
      category: "Main"
    },
    {
      id: "dish-6",
      name: "Rice & Peas",
      description: "Traditional Caribbean rice cooked with coconut.",
      category: "Side"
    },
    {
      id: "dish-7",
      name: "Plantain",
      description: "Sweet or savoury, cooked to perfection.",
      category: "Side"
    },
    {
      id: "dish-8",
      name: "Mac & Cheese",
      description: "Creamy, cheesy comfort in every bite.",
      category: "Side"
    }
  ];

  return (
    <div className="min-h-screen bg-cream page-transition">
      {/* Hero Section with Real Buffet Photo */}
      <section 
        ref={heroRef}
        className="relative h-96 flex items-center justify-center text-white"
        style={{
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/jz2vaed1_IMG_0100.jpeg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 image-overlay-luxury"></div>
        <div className={`relative z-10 text-center ${heroVisible ? 'animate-fade-in-up' : 'opacity-0'}`} data-testid="menu-hero">
          <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center mx-auto mb-4">
            <Utensils className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-shadow-luxury" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Our Menu</h1>
          <div className="w-24 h-1 bg-gold mx-auto mt-4"></div>
          <p className="text-2xl mt-6 italic">Authentic Afro Caribbean Cuisine, Served with Love</p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 px-4 bg-cream-dark text-center">
        <div className="max-w-3xl mx-auto">
          <p className="text-2xl text-gray-700 leading-relaxed italic">
            Every weekend, our menu changes slightly — just like Angella's Sunday table. But you can always count on an exquisite spread that makes you smile and fills your heart.
          </p>
        </div>
      </section>

      {/* Real Buffet Labels Photo */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="relative overflow-hidden rounded-lg shadow-luxury">
            <img 
              src="https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/gqawsiad_IMG_0096.jpeg" 
              alt="Buffet menu labels" 
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
              <div className="p-10 text-white">
                <h3 className="text-4xl font-bold mb-2" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Authentic Afro Caribbean Buffet</h3>
                <p className="text-xl">Prepared fresh with love and traditional recipes</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Signature Dishes */}
      <section className="py-24 px-4 bg-cream-dark" data-testid="signature-dishes">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="w-16 h-1 bg-gold mx-auto mb-6"></div>
            <h2 className="text-5xl font-bold text-brown mb-4" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Signature Dishes</h2>
            <p className="text-lg text-gray-600 italic">From Mama-Mam's kitchen to yours</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {dishes.map((dish) => (
              <div 
                key={dish.id} 
                className="bg-white rounded-lg shadow-luxury-hover overflow-hidden transition-all transform hover:-translate-y-2"
                data-testid={`dish-card-${dish.id}`}
              >
                <div className="p-8">
                  <div className="inline-block px-4 py-1 bg-gold-light text-brown text-sm font-semibold rounded-full mb-4">
                    {dish.category}
                  </div>
                  <h3 className="text-3xl font-bold mb-3 text-brown" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>{dish.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{dish.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* And More Section */}
          <div className="text-center mt-16">
            <div className="bg-gold-light p-8 rounded-lg shadow-luxury inline-block">
              <p className="text-3xl font-bold text-brown mb-3" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>...and much more!</p>
              <p className="text-lg text-gray-700">Our menu features a rotating selection of authentic Afro Caribbean dishes,<br />sides, desserts, and seasonal specialties.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Real Buffet Spread Photo */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="relative overflow-hidden rounded-lg shadow-luxury">
            <img 
              src="https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/tqhnalfr_IMG_0101.jpeg" 
              alt="Guests enjoying the buffet" 
              className="w-full h-[500px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Family Note */}
      <section className="py-20 px-4 bg-cream-dark text-center">
        <div className="max-w-3xl mx-auto bg-white p-12 rounded-lg shadow-luxury">
          <div className="w-12 h-1 bg-gold mx-auto mb-6"></div>
          <p className="text-2xl text-gray-700 italic leading-relaxed">
            Because family comes first, we always ensure there's something for everyone — from little ones to grandparents. Vegetarian and vegan options available upon request.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gold text-white text-center">
        <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Ready to Taste Home?</h2>
        <p className="text-xl mb-8 opacity-90">Experience authentic Afro Caribbean cuisine in an elegant setting</p>
        <Link 
          to="/reserve" 
          className="inline-block bg-white text-brown hover:bg-cream font-semibold px-10 py-4 rounded-full text-lg transition-all transform hover:scale-105 shadow-lg"
          data-testid="menu-cta-button"
        >
          Reserve Your Table →
        </Link>
      </section>
    </div>
  );
};

export default Menu;
